import React, { useState, useEffect } from 'react'
import { Card, Form, Button, Dropdown } from 'react-bootstrap'
import {
  FaPencilAlt,
  FaDice,
  FaFlask,
  FaEllipsisV,
  FaPlay,
  FaStop,
  FaGraduationCap,
  FaTrash,
} from 'react-icons/fa'
import styled from 'styled-components'
import { Model } from '../../types/models/model'
import { HyperParameterJSON, TrainParameters } from '../../types/models/hyperparameters'
import { adjectives, nouns } from '../../utils/modelNameWords'
import { useAppSelector } from '../../store/hooks'
import { useAppDispatch } from '../../store/hooks'
import { selectSelectedModelId } from '../../store/slices/modelSlice'
import {
  useDeleteModelMutation,
  useFetchModelsQuery,
  useFineTuneModelMutation,
  useGetModelQuery,
  useStopTrainModelMutation,
  useTrainModelMutation,
  useUpdateModelMutation,
} from '../../services/modelsApi'
import ConfirmDialog from '../modals/ConfirmDialog'
import ErrorDialog from '../modals/ErrorDialog'
import FineTuneModal from '../modals/FineTuneModal'
import {
  selectSelectedTrainingSource,
  selectSelectedValidationSource,
} from '../../store/slices/datasetSlice'
import { selectSelectedProjectId, selectFilterTags } from '../../store/slices/projectSlice'
import HyperparameterSettingsModal from '../modals/HyperparametersSettingsModal'

const ModelNameCard = styled(Card)`
  background: #2a2a2a;
  border: none;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  /* Allow dropdown menus to render outside without being clipped */
  overflow: visible;

  .card-body {
    padding: 0;
  }

  .model-info {
    padding: 1.25rem 1.5rem;
    background: #2a2a2a;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 768px) {
      padding: 1rem 1.25rem;
    }

    .model-details {
      flex: 1;
      min-width: 0;
      margin-right: 1rem;
    }

    .model-actions {
      flex-shrink: 0;
      display: flex;
      gap: 0.75rem;
      align-items: center;
      height: 100%;

      .d-flex {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        height: 100%;
      }

      .btn {
        height: 38px; /* Set a fixed height for all buttons */
        flex: 1;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        font-weight: 500;
        letter-spacing: 0.3px;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        min-width: 140px;
        white-space: nowrap; /* Prevent text wrapping */
        width: auto; /* Allow button to grow based on content */

        &:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        &:active {
          transform: translateY(1px);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }

        svg {
          width: 18px;
          height: 18px;
        }
      }

      .btn-parameters {
        background: rgba(255, 123, 0, 0.1) !important;
        border-color: rgba(255, 123, 0, 0.5) !important;
        color: rgba(255, 123, 0, 1) !important;

        &:hover {
          background: rgba(255, 123, 0, 0.2) !important;
          border-color: rgba(255, 123, 0, 0.7) !important;
        }

        &:active {
          background: rgba(255, 123, 0, 0.3) !important;
          border-color: rgba(255, 123, 0, 0.8) !important;
        }

        &:disabled {
          opacity: 0.6;
          transform: none;
          box-shadow: none;
        }
      }

      .btn-train {
        background: rgba(25, 135, 84, 0.1) !important;
        border-color: rgba(25, 135, 84, 0.5) !important;
        color: rgba(25, 135, 84, 1) !important;

        &:hover {
          background: rgba(25, 135, 84, 0.2) !important;
          border-color: rgba(25, 135, 84, 0.7) !important;
        }

        &:active {
          background: rgba(25, 135, 84, 0.3) !important;
          border-color: rgba(25, 135, 84, 0.8) !important;
        }
      }

      .btn-stop {
        background: rgba(220, 53, 69, 0.1) !important;
        border-color: rgba(220, 53, 69, 0.5) !important;
        color: rgba(220, 53, 69, 1) !important;

        &:hover {
          background: rgba(220, 53, 69, 0.2) !important;
          border-color: rgba(220, 53, 69, 0.7) !important;
        }

        &:active {
          background: rgba(220, 53, 69, 0.3) !important;
          border-color: rgba(220, 53, 69, 0.8) !important;
        }
      }

      .btn-finetune {
        background: rgba(138, 43, 226, 0.1) !important;
        border-color: rgba(138, 43, 226, 0.5) !important;
        color: rgba(138, 43, 226, 1) !important;

        &:hover {
          background: rgba(138, 43, 226, 0.2) !important;
          border-color: rgba(138, 43, 226, 0.7) !important;
        }

        &:active {
          background: rgba(138, 43, 226, 0.3) !important;
          border-color: rgba(138, 43, 226, 0.8) !important;
        }
      }
    }
  }

  .model-name-input {
    max-width: 300px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;

    &:focus {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
    }
  }
`

const SmallDiceIcon = styled(FaDice)`
  font-size: 1.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  color: white;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 1;
  }
`

const SmallPencilIcon = styled(FaPencilAlt)`
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  color: #4a90e2;
  position: absolute;
  top: -8px;
  left: -20px;
  background: #2a2a2a;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    opacity: 1;
  }
`

const StyledDropdown = styled(Dropdown)`
  .dropdown-toggle {
    background: transparent;
    border: none;
    padding: 0.2rem;
    color: rgba(255, 255, 255, 0.7);
    box-shadow: none !important;

    &:hover,
    &:focus {
      color: rgba(255, 255, 255, 1);
      background: transparent;
      box-shadow: none !important;
    }

    &::after {
      display: none;
    }
  }

  .dropdown-menu {
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: none;
    z-index: 2000; /* ensure menu appears above surrounding elements */

    .dropdown-item {
      color: rgba(255, 255, 255, 0.7);
      padding: 0.5rem 1rem;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 1);
      }

      &.text-danger {
        color: #dc3545 !important;

        &:hover {
          background: rgba(220, 53, 69, 0.1);
        }
      }
    }
  }
`

const ModelHeader: React.FC<{ showParameters?: boolean }> = ({
  showParameters: showPerformance = true,
}) => {
  // Debug: Listen for logout events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token' && !e.newValue) {
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [isEditingName, setIsEditingName] = useState(false)
  const [showFineTuneModal, setShowFineTuneModal] = useState(false)
  const [showHyperparametersModal, setShowHyperparametersModal] = useState(false)
  const dispatch = useAppDispatch()
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const [updateModel] = useUpdateModelMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const selectedTrainingSources = useAppSelector(selectSelectedTrainingSource)
  const validationSources = useAppSelector(selectSelectedValidationSource)
  const selectedFilterTags = useAppSelector(selectFilterTags)
  const [deleteModel] = useDeleteModelMutation()
  const [trainModel] = useTrainModelMutation()
  const [stopTrainModel] = useStopTrainModelMutation()
  const [fineTuneModel] = useFineTuneModelMutation()
  const [isFineTuning, setIsFineTuning] = useState(false)

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModelName(e.target.value)
  }

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    pollingInterval: 60000,
  })

  const { data: models } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId,
      pollingInterval: 60000,
    }
  )

  const [modelName, setModelName] = useState(selectedModel?.name || '')

  const handleActionButtonClick = () => {
    const modelState = selectedModel?.state.toLowerCase()

    if (modelState === 'initialized' || modelState === 'err') {
      handleTrainModel()
    } else if (modelState === 'training') {
      handleStopTraining()
    } else if (modelState === 'trained') {
      setShowFineTuneModal(true)
    }
  }

  const handleTrainModel = async () => {
    if (!selectedModelId || !selectedModel) {
      setErrorMessage('No model selected')
      setShowError(true)
      return
    }

    if (!selectedTrainingSources?.length) {
      setErrorMessage('No training sources selected')
      setShowError(true)
      return
    }

    try {
      const modelTrainParams = {
        id: selectedModelId,
        train_metadata_filter:
          selectedTrainingSources && selectedTrainingSources.length > 0
            ? { datasource: selectedTrainingSources }
            : undefined,
        validation_metadata_filter:
          validationSources && validationSources.length > 0
            ? { datasource: validationSources }
            : undefined,
        tag_filter:
          selectedFilterTags && selectedFilterTags.length > 0
            ? selectedFilterTags.map((t) => t.id)
            : undefined,
      }

      trainModel(modelTrainParams)
    } catch (err: any) {
      setErrorMessage(
        err ? (err as any).data?.message || 'Failed to train model' : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const handleStopTraining = async () => {
    if (selectedModel) {
      try {
        dispatch(stopTrainModel({ id: selectedModelId || '' }))
      } catch (err) {
        setErrorMessage(
          err
            ? (err as any).data?.message || 'Failed to stop training'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  const generateFineTuneName = () => {
    if (!selectedModel?.name) return ''
    const randomChars = Math.random().toString(36).substring(2, 4).toUpperCase()
    return `${selectedModel.name}-${randomChars}`
  }

  const handleFineTuneModel = async () => {
    if (!selectedModelId || !selectedModel) {
      setErrorMessage('No model selected')
      setShowError(true)
      return
    }

    try {
      setIsFineTuning(true)
      // Get original dataset sources from localStorage
      const originalDatasetSources = JSON.parse(localStorage.getItem(selectedModelId) || '[]')

      // Combine original and current sources, removing duplicates
      const combinedTrainingSources = Array.from(
        new Set([...(originalDatasetSources?.datasource || []), ...(selectedTrainingSources || [])])
      )

      const combinedValidationSources = Array.from(
        new Set([
          ...(originalDatasetSources?.validation_datasource || []),
          ...(validationSources || []),
        ])
      )

      // const updatedModel = await updateModel({
      //   id: selectedModelId,
      //   modelData: { name: modelName, description: selectedModel.description, parameters: selectedModel.parameters },
      // }).unwrap()
      // dispatch(setSelectedModelId(updatedModel.id))
      const fineTunedModel = await fineTuneModel({
        id: selectedModelId,
        name: generateFineTuneName(),
        epochs: 5,
        freeze: 10,
        train_metadata_filter: { datasource: combinedTrainingSources },
        validation_metadata_filter: { datasource: combinedValidationSources },
      }).unwrap()
      setShowFineTuneModal(false)
    } catch (err) {
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to update model'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    } finally {
      setIsFineTuning(false)
    }
  }

  const initiateDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleSaveName = async () => {
    if (modelName !== selectedModel?.name) {
      await handleUpdateModel(modelName)
    }
    setIsEditingName(false)
  }

  const handleCancelEdit = () => {
    setModelName(selectedModel?.name || '')
    setIsEditingName(false)
  }

  const handleDeleteModel = async () => {
    if (!selectedModelId || !selectedModel?.projectid) return

    try {
      // Delete the current model
      await deleteModel({ id: selectedModelId }).unwrap()

      // Find the next most recent model
      const nextModel = models?.models
        .filter((m: Model) => m.id !== selectedModelId) // Exclude the deleted model
        .sort(
          (a: Model, b: Model) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0] // Sort by created_at desc

      // No need to select a model after deletion
    } catch (err: any) {
      console.error('Model deletion failed:', err)
      console.error('Deletion error details:', {
        status: err?.status,
        data: err?.data,
        message: err?.message,
      })
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to delete model'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
    setShowDeleteConfirm(false)
  }

  const handleRandomName = () => {
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    setModelName(`${randomAdjective} ${randomNoun}`)
  }

  const handleUpdateModel = async (newName: string) => {
    if (selectedModel) {
      try {
        const updatedModel = await updateModel({
          id: selectedModelId || '',
          modelData: {
            name: newName,
            description: selectedModel?.description || '',
            parameters: selectedModel?.parameters || ({} as TrainParameters),
          },
        }).unwrap()
      } catch (err) {
        setErrorMessage(
          err
            ? (err as any).data?.message || 'Failed to update model name'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  const handleSaveTrainingParameters = async (newParameters: TrainParameters) => {
    if (selectedModel) {
      try {
        const updatedModel = await updateModel({
          id: selectedModel.id,
          modelData: {
            name: selectedModel.name,
            parameters: {
              ...selectedModel.parameters,
              hyperparameters: newParameters.hyperparameters as HyperParameterJSON,
            },
            description: selectedModel.description,
          },
        }).unwrap()
        setShowHyperparametersModal(false)
      } catch (err) {
        setErrorMessage(
          err
            ? (err as any).data?.message || 'Failed to update model parameters'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  return (
    <>
      <ModelNameCard>
        <Card.Body>
          <div className="model-info">
            <div className="model-details">
              {isEditingName ? (
                <div className="d-flex align-items-center gap-2">
                  <SmallDiceIcon onClick={handleRandomName} />
                  <Form.Control
                    type="text"
                    value={modelName}
                    onChange={handleNameChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSaveName()
                      }
                    }}
                    autoFocus
                    className="model-name-input"
                  />
                  <Button variant="outline-success" size="sm" onClick={handleSaveName}>
                    Save
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-start">
                    <h2 className="mb-1 card-title text-white" style={{ position: 'relative' }}>
                      <SmallPencilIcon onClick={() => setIsEditingName(true)} />
                      {selectedModel?.name || 'Untitled Model'}
                    </h2>
                  </div>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                    }}
                  >
                    Created:{' '}
                    {selectedModel?.created_at
                      ? new Date(selectedModel.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </div>
                </div>
              )}
            </div>
            <div className="model-actions">
              <div className="d-flex align-items-center gap-2">
                {showPerformance && (
                  <Button
                    variant="outline-light"
                    className="btn-parameters"
                    onClick={() => setShowHyperparametersModal(true)}
                    disabled={!selectedModel || selectedModel.state.toLowerCase() === 'training'}
                  >
                    <FaFlask className="me-2" />
                    Parameters
                  </Button>
                )}
                <Button
                  variant="outline-light"
                  className={
                    selectedModel?.state.toLowerCase() === 'training'
                      ? 'btn-stop'
                      : selectedModel?.state.toLowerCase() === 'trained'
                      ? 'btn-finetune'
                      : 'btn-train'
                  }
                  onClick={handleActionButtonClick}
                >
                  {selectedModel?.state.toLowerCase() === 'initialized' ||
                  selectedModel?.state.toLowerCase() === 'err' ? (
                    <FaPlay className="me-2" />
                  ) : selectedModel?.state.toLowerCase() === 'training' ? (
                    <FaStop className="me-2" />
                  ) : (
                    <FaGraduationCap className="me-2" />
                  )}
                  {selectedModel?.state.toLowerCase() === 'initialized' ||
                    (selectedModel?.state.toLowerCase() === 'err' && 'Train')}
                  {selectedModel?.state.toLowerCase() === 'training' && 'Stop Training'}
                  {selectedModel?.state.toLowerCase() === 'trained' && 'Fine-Tune'}
                </Button>
                <StyledDropdown>
                  <Dropdown.Toggle disabled={selectedModel?.state.toLowerCase() === 'training'}>
                    <FaEllipsisV />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item className="text-danger" onClick={initiateDelete}>
                      <FaTrash className="me-2" />
                      Delete Model
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </StyledDropdown>
              </div>
            </div>
          </div>
        </Card.Body>
      </ModelNameCard>

      <HyperparameterSettingsModal
        show={showHyperparametersModal}
        handleClose={() => setShowHyperparametersModal(false)}
        onSave={handleSaveTrainingParameters}
        initialHyperparameters={selectedModel?.parameters?.hyperparameters}
      />

      <FineTuneModal
        show={showFineTuneModal}
        onHide={() => setShowFineTuneModal(false)}
        model={selectedModel}
        onFineTune={handleFineTuneModel}
        isFineTuning={isFineTuning}
      />

      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirmRight={handleDeleteModel}
        title="Delete Model"
        message={`Are you sure you want to delete model "${selectedModel?.name}"? This action cannot be undone.`}
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage || 'An unexpected error occurred'}
        title="Error"
      />
    </>
  )
}

export default ModelHeader
